// routes/appRoutes.jsx
import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Outlet } from 'react-router-dom';

// -------------------------------------------------------------
// Core Components
// -------------------------------------------------------------
import ProtectedRoute from '../Components/ProtectedRoute';

// -------------------------------------------------------------
// Lazy Loaded Pages
// -------------------------------------------------------------
const Login = lazy(() => import('../Pages/App/Login'));
const AppDashboard = lazy(() => import('../Pages/App/AppDashboard'));

// -- Profile
const ProfileApp = lazy(() => import('../Pages/App/Profile/ProfileDashboard'));
const UpdateProfileLayout = lazy(
  () => import('../Pages/App/Profile/UpdateProfile/UpdateProfileLayout'),
);
const UpdateDiriApp = lazy(
  () => import('../Pages/App/Profile/UpdateProfile/UpdateDiri'),
);
const UpdateMobilisasiApp = lazy(
  () => import('../Pages/App/Profile/UpdateProfile/Mobilisasi'),
);
const UpdateMinatBakatDiriApp = lazy(
  () => import('../Pages/App/Profile/UpdateProfile/MinatBakatDiri'),
);
const KesibukanKegiatan = lazy(
  () => import('../Pages/App/Profile/UpdateProfile/KesibukanKegiatan'),
);
const UpdateMediaSosialApp = lazy(
  () => import('../Pages/App/Profile/UpdateProfile/MediaSosial'),
);

// -- Publication
const PublicationAppIndex = lazy(
  () => import('../Pages/App/Publication/PublicationDashboard'),
);
const PublicationAppAdd = lazy(
  () => import('../Pages/App/Publication/PublicationAdd'),
);
const PublicationAppDetail = lazy(
  () => import('../Pages/App/Publication/PublicationDetail'),
);
const PublicationAppEdit = lazy(
  () => import('../Pages/App/Publication/PublicationEdit'),
);

// -- Evaluation
const AppEvaluationList = lazy(
  () => import('../Pages/App/Evaluation/EvaluationList'),
);
const AppEvaluationDetail = lazy(
  () => import('../Pages/App/Evaluation/EvaluationDetail'),
);
const AppEvaluationAdd = lazy(
  () => import('../Pages/App/Evaluation/EvaluationAdd'),
);
const AppEvaluationFinal = lazy(
  () => import('../Pages/App/Evaluation/EvaluationFinal'),
);
const CertificateLanding = lazy(
  () => import('../Pages/App/Evaluation/CertificateLanding'),
);

// -- Capacity Building
const CapacityBuildingList = lazy(
  () => import('../Pages/App/CapacityBuilding/CapacityBuildingList'),
);
const CapacityBuildingDetail = lazy(
  () => import('../Pages/App/CapacityBuilding/CapacityBuildingDetail'),
);
const CapacityBuildingTest = lazy(
  () => import('../Pages/App/CapacityBuilding/CapacityBuildingTest'),
);

// -- Attendances
const AttendancesDetail = lazy(
  () => import('../Pages/App/Attendances/AttendancesDetail'),
);
const ClockAttendance = lazy(
  () => import('../Pages/App/Attendances/ClockAttendance'),
);
const AttendancesHistory = lazy(
  () => import('../Pages/App/Attendances/AttendancesHistory'),
);

// -- BUMDesa Base
const BumDESAIndex = lazy(() => import('../Pages/App/Bumdes/BumdesDashboard'));

// -- Survey VECI
const SurveyVECILanding = lazy(
  () => import('../Pages/App/Bumdes/SurveyVECI/SurveyLanding'),
);
const SurveyVECITake = lazy(
  () => import('../Pages/App/Bumdes/SurveyVECI/SurveyVECITake'),
);
const SurveyVECIDetail = lazy(
  () => import('../Pages/App/Bumdes/SurveyVECI/SurveyDetail'),
);
const SurveyVECIComparison = lazy(
  () => import('../Pages/App/Bumdes/SurveyVECI/SurveyComparison'),
);
const SurveyVECISuccess = lazy(
  () => import('../Pages/App/Bumdes/SurveyVECI/SurveySuccess'),
);

// -- Survey Leadership
const LeadershipDashboard = lazy(
  () => import('../Pages/App/Bumdes/SurveyLeadership/LeadershipDashboard'),
);
const SurveyLeadershipTake = lazy(
  () => import('../Pages/App/Bumdes/SurveyLeadership/SurveyLeadershipTake'),
);

// -- BMC
const BMCLanding = lazy(() => import('../Pages/App/Bumdes/BMC/BMCLanding'));
const BMCForm = lazy(() => import('../Pages/App/Bumdes/BMC/BMCForm'));
const BMCVisualizer = lazy(
  () => import('../Pages/App/Bumdes/BMC/BMCVisualizer'),
);
const BMCSuccess = lazy(() => import('../Pages/App/Bumdes/BMC/BMCSuccess'));
const BMCVersionDetail = lazy(
  () => import('../Pages/App/Bumdes/BMC/BMCVersionDetail'),
);

// -- Red Cloud PoC
const RedCloudPoCLayout = lazy(
  () => import('../Pages/App/Bumdes/RedCloud/RedCloudPoCLayout'),
);
const RedCloudPhase1 = lazy(
  () => import('../Pages/App/Bumdes/RedCloud/RedCloudPhase1'),
);
const RedCloudPhase2 = lazy(
  () => import('../Pages/App/Bumdes/RedCloud/RedCloudPhase2'),
);
const RedCloudPhase3 = lazy(
  () => import('../Pages/App/Bumdes/RedCloud/RedCloudPhase3'),
);

const FallbackLoader = () => (
  <div
    className="d-flex justify-content-center align-items-center py-5"
    style={{ minHeight: '50vh' }}
  >
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const appRoutes = (authUser) => (
  <>
    <Route path="app">
      <Route
        path="login"
        element={
          authUser && authUser?.activeSession?.roleCode === 'STUDENT' ? (
            <Navigate to="/app" replace />
          ) : (
            <Suspense fallback={<FallbackLoader />}>
              <Login />
            </Suspense>
          )
        }
      />

      {/* PoC Routes (Bypass Auth for easy testing) */}
      <Route
        path="poc/red-cloud"
        element={
          <Suspense fallback={<FallbackLoader />}>
            <RedCloudPoCLayout />
          </Suspense>
        }
      >
        <Route index element={<Navigate to="problem-collection" replace />} />
        <Route path="problem-collection" element={<RedCloudPhase1 />} />
        <Route
          path="categorization-prioritization"
          element={<RedCloudPhase2 />}
        />
        <Route path="listing" element={<RedCloudPhase3 />} />
      </Route>

      {/* Semua route di bawah ini butuh login (ProtectedRoute) */}
      <Route element={<ProtectedRoute allowedRoles={['Mahasiswa']} />}>
        <Route
          element={
            <Suspense fallback={<FallbackLoader />}>
              <Outlet />
            </Suspense>
          }
        >
          <Route index element={<AppDashboard authUser={authUser} />} />

          {/* Profile */}
          <Route path="profile">
            <Route index element={<ProfileApp />} />
            <Route
              path="update"
              element={<UpdateProfileLayout authUser={authUser} />}
            >
              <Route
                path="identitas"
                element={<UpdateDiriApp authUser={authUser} />}
              />
              <Route path="mobilisasi" element={<UpdateMobilisasiApp />} />
              <Route
                path="interest-talent"
                element={<UpdateMinatBakatDiriApp />}
              />
              <Route path="acivities" element={<KesibukanKegiatan />} />
              <Route path="social-media" element={<UpdateMediaSosialApp />} />
            </Route>
          </Route>

          {/* Publication */}
          <Route path="publication">
            <Route index element={<PublicationAppIndex />} />
            <Route path="add" element={<PublicationAppAdd />} />
            <Route path=":id/edit" element={<PublicationAppEdit />} />
            <Route path=":id" element={<PublicationAppDetail />} />
          </Route>

          <Route path="capacity-building">
            <Route index element={<CapacityBuildingList />} />
            <Route path=":id">
              <Route index element={<CapacityBuildingDetail />} />
              <Route path="test/:testId" element={<CapacityBuildingTest />} />
            </Route>
          </Route>

          <Route path="attendances">
            <Route index element={<ClockAttendance />} />
            <Route path="history" element={<AttendancesHistory />} />
            <Route path=":userId" element={<AttendancesDetail />} />
            <Route path="clock" element={<ClockAttendance />} />
          </Route>

          {/* Evaluation — 3 konteks setara (PAT, Final, Sertifikat), jadi
              masing-masing punya segment path eksplisit sendiri. Sebelumnya
              PAT "menumpang" di index /app/evaluation tanpa nama, bikin
              rancu dibanding /app/evaluation/final & /certificate-landing
              yang sudah bernama. */}
          <Route path="evaluation">
            <Route index element={<Navigate to="pat" replace />} />
            <Route path="pat">
              <Route index element={<AppEvaluationList />} />
              <Route path=":id" element={<AppEvaluationDetail />} />
              <Route path=":id/evaluate" element={<AppEvaluationAdd />} />
            </Route>
            <Route path="final" element={<AppEvaluationFinal />} />
            <Route path="certificate" element={<CertificateLanding />} />
            {/* Redirect path lama, jaga-jaga ada bookmark/link tersimpan */}
            <Route
              path="certificate-landing"
              element={<Navigate to="../certificate" replace />}
            />
          </Route>
        </Route>
      </Route>

      <Route
        element={
          <ProtectedRoute allowedRoles={['Mahasiswa', 'Project Officer']} />
        }
      >
        <Route
          element={
            <Suspense fallback={<FallbackLoader />}>
              <Outlet />
            </Suspense>
          }
        >
          <Route path="bumdes">
            <Route index element={<BumDESAIndex />} />
            <Route path="survey-veci">
              <Route index element={<SurveyVECILanding />} />
              <Route path="take" element={<SurveyVECITake />} />
              <Route path="results/:surveyId" element={<SurveyVECIDetail />} />
              <Route path="comparison" element={<SurveyVECIComparison />} />
              <Route path="success" element={<SurveyVECISuccess />} />
            </Route>

            <Route path="survey-leadership">
              <Route index element={<LeadershipDashboard />} />
              <Route path="take" element={<SurveyLeadershipTake />} />
            </Route>

            <Route path="bmc">
              <Route index element={<BMCLanding />} />
              <Route path="take" element={<BMCForm />} />
              <Route path="visualizer" element={<BMCVisualizer />} />
              <Route path="versions/:version" element={<BMCVersionDetail />} />
              <Route path="success" element={<BMCSuccess />} />
            </Route>
          </Route>
        </Route>
      </Route>
    </Route>
  </>
);

export default appRoutes;
